// src/services/visualizationParser.ts

import type { ChatMessage } from '../types/chat';
import { claudeService } from './claudeService';
import logger from '../config/logger';

export interface Visualization {
  type: 'text' | 'shape' | 'measurement';
  content: string;
  position: { x: number; y: number };
  style?: {
    color?: string;
    backgroundColor?: string;
    borderColor?: string;
    fontSize?: number;
    opacity?: number;
  };
}

export interface ParsedVisualization {
  visualizations: Visualization[];
  stockSymbols: string[];
  timeRange: string;
  insights: string[];
  keyLevels: number[];
}

const VISUALIZATION_PROMPT = `Analyze the market data and return a visualization specification as a JSON object.
The JSON must follow this exact format:
{
  "visualizations": [
    {
      "type": "text",
      "content": "string",
      "position": {
        "timeframe": number, // 0-100 representing percentage position in time series
        "priceLevel": number // actual price value
      },
      "style": {
        "color": string,
        "backgroundColor": string,
        "fontSize": number
      }
    }
  ]
}

Guidelines:
- Use timeframe as a percentage (0-100) to indicate position in the chart
- 0 = start of time period, 100 = end of time period
- Use actual price values for priceLevel
- Keep annotations concise and meaningful
- Use colors to indicate sentiment (green=bullish, red=bearish)`;

class VisualizationParser {
  private static instance: VisualizationParser;
  private claudeService?: typeof claudeService;

  private constructor() {}

  static getInstance(): VisualizationParser {
    if (!VisualizationParser.instance) {
      VisualizationParser.instance = new VisualizationParser();
    }
    return VisualizationParser.instance;
  }

  setClaudeService(service: typeof claudeService) {
    this.claudeService = service;
  }

  async parseResponse(message: any): Promise<any> {
    try {
      const response = await claudeService.generateVisualizations({
        message,
        stockData: message.charts,
        analysis: message.content
      });
  
      const cleanJson = response.replace(/```json\n?|\n?```/g, '').trim();
      const parsed = typeof cleanJson === 'string' ? JSON.parse(cleanJson) : cleanJson;
  
      // Validate and normalize the visualizations
      if (!Array.isArray(parsed.visualizations)) {
        throw new Error('Invalid visualization format');
      }
  
      // Ensure each visualization has required properties
      const normalizedVisualizations = parsed.visualizations.map((viz: any) => ({
        type: viz.type || 'text',
        content: viz.content || '',
        position: {
          timeframe: viz.position?.timeframe ?? 50, // Default to middle
          priceLevel: viz.position?.priceLevel,
          x: viz.position?.x ?? 50,
          y: viz.position?.y ?? 50
        },
        style: {
          color: viz.style?.color || '#FFFFFF',
          backgroundColor: viz.style?.backgroundColor || 'rgba(0,0,0,0.5)',
          fontSize: viz.style?.fontSize || 14,
          opacity: viz.style?.opacity || 1
        }
      }));
  
      return {
        ...parsed,
        visualizations: normalizedVisualizations
      };
    } catch (error) {
      console.error('Failed to parse visualization:', error);
      return {
        visualizations: [],
        insights: [],
        keyLevels: []
      };
    }
  }

  getDefaultStyles() {
    return {
      text: {
        color: '#FFFFFF',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderColor: '#3B82F6',
        fontSize: 14,
        opacity: 1
      },
      shape: {
        color: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderColor: '#3B82F6',
        opacity: 0.8
      },
      measurement: {
        color: '#10B981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        borderColor: '#10B981',
        opacity: 0.9
      }
    };
  }
}

export const visualizationParser = VisualizationParser.getInstance();